function normalizeSearchText(value = '') {
  return String(value || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function tokenizeSearchText(value = '') {
  const normalized = normalizeSearchText(value);
  if (!normalized) return [];
  return normalized.split(' ').filter(Boolean);
}

function buildSearchableText(parts = []) {
  return (Array.isArray(parts) ? parts : [parts])
    .flat()
    .map((part) => String(part || '').trim())
    .filter(Boolean)
    .join(' ');
}

function levenshteinDistance(left = '', right = '') {
  const a = String(left || '');
  const b = String(right || '');

  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;

  const previousRow = new Array(b.length + 1);
  const currentRow = new Array(b.length + 1);

  for (let j = 0; j <= b.length; j += 1) {
    previousRow[j] = j;
  }

  for (let i = 1; i <= a.length; i += 1) {
    currentRow[0] = i;

    for (let j = 1; j <= b.length; j += 1) {
      const insertion = currentRow[j - 1] + 1;
      const deletion = previousRow[j] + 1;
      const substitution = previousRow[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1);
      currentRow[j] = Math.min(insertion, deletion, substitution);
    }

    for (let j = 0; j <= b.length; j += 1) {
      previousRow[j] = currentRow[j];
    }
  }

  return previousRow[b.length];
}

function getTokenTolerance(queryToken = '') {
  const length = String(queryToken || '').length;
  if (length <= 3) return 0;
  if (length <= 5) return 1;
  if (length <= 8) return 2;
  return 3;
}

function scoreTokenMatch(queryToken = '', candidateToken = '') {
  const normalizedQuery = normalizeSearchText(queryToken);
  const normalizedCandidate = normalizeSearchText(candidateToken);
  if (!normalizedQuery || !normalizedCandidate) return 0;
  if (normalizedQuery === normalizedCandidate) return 1;
  if (normalizedCandidate.startsWith(normalizedQuery)) return 0.98;
  if (normalizedCandidate.includes(normalizedQuery)) return 0.9;
  if (normalizedQuery.includes(normalizedCandidate)) return 0.85;

  const tolerance = getTokenTolerance(normalizedQuery);
  if (tolerance === 0) return 0;

  const distance = levenshteinDistance(normalizedQuery, normalizedCandidate);
  if (distance > tolerance) return 0;

  const maxLength = Math.max(normalizedQuery.length, normalizedCandidate.length);
  return Math.max(0, 1 - distance / Math.max(1, maxLength));
}

function scoreSearchText(haystack = '', query = '') {
  const normalizedHaystack = normalizeSearchText(haystack);
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedHaystack || !normalizedQuery) return 0;

  if (normalizedHaystack === normalizedQuery) {
    return 1000;
  }

  if (normalizedHaystack.includes(normalizedQuery)) {
    return 900 + normalizedQuery.length / Math.max(1, normalizedHaystack.length);
  }

  const queryTokens = tokenizeSearchText(normalizedQuery);
  const haystackTokens = tokenizeSearchText(normalizedHaystack);
  if (!queryTokens.length || !haystackTokens.length) return 0;

  let totalScore = 0;
  let matchedTokens = 0;

  for (const queryToken of queryTokens) {
    let bestScore = 0;
    for (const haystackToken of haystackTokens) {
      const candidateScore = scoreTokenMatch(queryToken, haystackToken);
      if (candidateScore > bestScore) {
        bestScore = candidateScore;
        if (bestScore >= 1) break;
      }
    }

    if (bestScore <= 0) return 0;

    matchedTokens += 1;
    totalScore += bestScore;
  }

  const coverage = matchedTokens / queryTokens.length;
  const averageScore = totalScore / queryTokens.length;
  return coverage * 60 + averageScore * 40;
}

function filterAndRankByQuery(items = [], query = '', getSearchText = () => '') {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) return Array.isArray(items) ? items : [];

  return (Array.isArray(items) ? items : [])
    .map((item, index) => ({
      item,
      index,
      score: scoreSearchText(getSearchText(item, index), normalizedQuery),
    }))
    .filter((entry) => entry.score > 0)
    .sort((left, right) => right.score - left.score || left.index - right.index)
    .map((entry) => entry.item);
}

function buildProductSearchText(product = {}, typeMetaOrMap = null) {
  const typeKey = String(product?.productType || '').trim();
  const typeMeta =
    typeMetaOrMap && typeof typeMetaOrMap.get === 'function' ? typeMetaOrMap.get(typeKey) || null : typeMetaOrMap;
  const fields = typeMeta?.fields || typeMeta?.fieldLabelByKey || new Map();
  const specs =
    product?.specs && typeof product.specs === 'object' && !Array.isArray(product.specs) ? product.specs : {};

  const specsSearch = Object.entries(specs)
    .map(([key, value]) => {
      const fieldMeta = fields.get ? fields.get(key) : null;
      const fieldLabel = fieldMeta?.label || fieldMeta?.name || fieldMeta || key;
      return `${fieldLabel} ${String(value ?? '')}`;
    })
    .join(' ');

  const productBrand =
    String(specs.brand || specs.manufacturer || specs.make || product?.brand || '').trim();

  return buildSearchableText([
    product?.name || '',
    product?.slug || '',
    product?.description || '',
    product?.productType || '',
    typeMeta?.label || '',
    productBrand,
    product?.price,
    product?.stock,
    specsSearch,
    ...Object.values(product?.specsI18n?.en || {}),
    ...Object.values(product?.specsI18n?.fr || {}),
    product?.translations?.en?.name || '',
    product?.translations?.fr?.name || '',
    product?.translations?.en?.description || '',
    product?.translations?.fr?.description || '',
  ]);
}

function buildSparePartSearchText(sparePart = {}) {
  return buildSearchableText([
    sparePart?.name || '',
    sparePart?.slug || '',
    sparePart?.description || '',
    sparePart?.categoryKey || '',
    sparePart?.category || '',
    sparePart?.price,
    sparePart?.stock,
    sparePart?.translations?.en?.name || '',
    sparePart?.translations?.fr?.name || '',
    sparePart?.translations?.en?.description || '',
    sparePart?.translations?.fr?.description || '',
    sparePart?.categoryI18n?.en || '',
    sparePart?.categoryI18n?.fr || '',
  ]);
}

module.exports = {
  normalizeSearchText,
  tokenizeSearchText,
  buildSearchableText,
  levenshteinDistance,
  scoreTokenMatch,
  scoreSearchText,
  filterAndRankByQuery,
  buildProductSearchText,
  buildSparePartSearchText,
};
